import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import { PercentageIcon, PrintIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import {
  getAffectedStaffTotalSalariesWithoutPas,
  getAffectedStaffTotalSalariesWithPas
} from '../../api/affected-staffs/get-affected-staff-salary-total'
import { updateAffectedStaffPas } from '../../api/affected-staffs/update-affected-staff-pas'
import { uploadAffectedStaffSalariesExcel } from '../../api/affected-staffs/upload-affected-staff-salaries-sheet'
import { API } from '../../api/apis.config'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import { UploadCsvModal } from '../../components/elements/modals/upload-csv-modal'
import UserNavBar from '../../components/elements/nav-bar'
import { getAffectedStaffSalaryColumns } from '../../components/pages/affected-staffs/affected-staff-salary.utils'
import { AffectedStaffsSalaryFilters } from '../../components/pages/affected-staffs/affected-staffs-salary-filters'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { useUrl } from '../../lib/hooks/use-url'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import {
  AffectedStaffMap,
  AffectedStaffSalaryFilter
} from '../../lib/types/models/affectedStaff/affected-staff'
import {
  transformAffectedStaffSalaryFiltersToUrl,
  transformUrlToAffectedStaffSalaryFilters
} from '../../lib/utils/filters-mapping/affected-staff-salary'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

export function AccountantAffectedStaffSalariesListPage() {
  const tableRef = useRef<DynamicTableRef<AffectedStaffMap['SALARY']>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { readPagination } = useUrl()
  const [search, setSearch] = useState(() => readPagination().q)
  const { modalState, toggleModal, closeModel } = useModalManager<'filters' | 'upload-csv'>([
    'filters',
    'upload-csv'
  ])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<AffectedStaffSalaryFilter>({
      initialFilters: {},
      options: { allowSearchParam: true },
      transformFilterToUrl: transformAffectedStaffSalaryFiltersToUrl,
      transformUrlToFilter: transformUrlToAffectedStaffSalaryFilters
    })

  const { data: totalSalariesWithPas, refetch: refetchTotalSalariesWithPas } = useQuery({
    queryKey: ['affected-staff-salaries-total-with-pas', JSON.stringify(selectedFilter), search],
    queryFn: () => getAffectedStaffTotalSalariesWithPas(selectedFilter, search, true)
  })

  const { mutateAsync: updateAffectedStaffPasMutation } = useMutation({
    mutationFn: updateAffectedStaffPas,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      refetchTotalSalariesWithPas()
      toast.success('Le PAS a été mis à jour avec succès')
    }
  })

  const { mutateAsync: uploadAffectedStaffSalariesExcelMutation } = useMutation({
    mutationFn: uploadAffectedStaffSalariesExcel,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      refetchTotalSalariesWithPas()
      toast.success('Le fichier a été importé avec succès')
    }
  })

  const { data: totalSalariesWithoutPas } = useQuery({
    queryKey: [
      'affected-staff-salaries-total-without-pas',
      JSON.stringify(selectedFilter),
      search
    ],
    queryFn: () => getAffectedStaffTotalSalariesWithoutPas(selectedFilter, search, true)
  })

  return (
    <div>
      <UserNavBar title={'Saisie du PAS'} icon={PercentageIcon} />
      <div className="h-4" />

      <DynamicDataTable<AffectedStaffMap['SALARY'], AffectedStaffSalaryFilter>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        title={'Saisie du PAS'}
        columns={getAffectedStaffSalaryColumns({
          role: RoleEnum.ACCOUNTANT,
          handlePasChange: async (pas, affectedStaff) =>
            await updateAffectedStaffPasMutation({ id: affectedStaff.id, pas })
        })}
        allowSearchParam
        onSearchChange={setSearch}
        specialRows={[
          { color: tailwindColors.grey.light.DEFAULT, condition: ({ pasBlocked }) => pasBlocked }
        ]}
        onFiltersClick={() => toggleModal('filters')}
        endpoint={API.affectedStaffs.getAffectedStaffsSalaries.url}
        // UK staff have no PAS, the backend leaves them out of this screen
        extraParams={{ pasEntry: true }}
        permissions={{ delete: false }}
        footerclassName="bg-primary-light"
        footerCells={[
          {
            accessorKey: 'id',
            value: (
              <Typography variant="caption" color={tailwindColors.primary.DEFAULT}>
                Total
              </Typography>
            )
          },
          {
            accessorKey: 'totalSalary',
            value: (
              <div className="flex flex-col gap-2 py-2">
                {totalSalariesWithoutPas?.totals.map(({ currency, total }) => (
                  <Badge
                    key={currency}
                    variant="orange"
                    text={NumberFormatter.formatCurrency(total, currency)}
                  />
                ))}
              </div>
            )
          },
          {
            accessorKey: 'viewData.totalSalaryAfterPas',
            value: (
              <div className="flex flex-col gap-2 py-2">
                {totalSalariesWithPas?.totals.map(({ currency, total }) => (
                  <Badge
                    key={currency}
                    variant="orange"
                    text={NumberFormatter.formatCurrency(total, currency)}
                  />
                ))}
              </div>
            )
          }
        ]}
        actions={[
          {
            type: 'global',
            icon: PrintIcon,
            label: 'Importer Excel des salaires apres PAS',
            variant: 'ghost',
            fn: () => toggleModal('upload-csv')
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <AffectedStaffsSalaryFilters
          type={'simple'}
          filter={filter}
          onFilterChange={onFilterChange}
        />
      </FilterDrawer>

      <UploadCsvModal
        title={'Importer les salaires des personnels affectés'}
        isOpen={modalState['upload-csv']}
        onClose={() => closeModel('upload-csv')}
        onUpload={async (file) => {
          await uploadAffectedStaffSalariesExcelMutation(file)
        }}
      />
    </div>
  )
}
